import { UserRoles, internalRoles } from '@models/user';

export enum PortalAdminPageName {
  Offering = 'Offering Management',
  Brokerage = 'Brokerage Management',
  User = 'User Management',
  Maintenance = 'Maintenance',
  Alerts = 'Alerts',
  UWMgmt = 'Underwriter Management',
  QuoteSetupPresetManagement = 'Quote Setup Preset Management',
  MoratoriumManagement = 'Moratorium Management',
  AdvancedPortalData = 'Advanced Portal Configuration',
  FormsV2Admin = 'FormsV2 Admin',
  SubmissionDrafts = 'Submission Drafts',
  UiPlayground = 'UI Playground',
  BORTransfer = 'BOR Transfer Requests',
  ApiClientManagement = 'API Client Management',
  GetAppointedPages = 'Get Appointed Pages',
  SalesAssignmentRules = 'Sales Assignment Rules',
  BrokerageOnboarding = 'Brokerage Onboarding',
  BoxSignTemplates = 'Box Sign Templates',
}

export enum PortalAdminAdvancedPortalDataPageName {
  LOIAvailability = 'LOI Availability',
  BrokerageCommission = 'Brokerage Commission Defaults',
  UnderwritingActivityTypes = 'Underwriting Activity Types',
  OfferingDefaultJurisdiction = 'Offering Default Jurisdictions',
  OfferingSubjectivities = 'Subjectivities',
  BusinessClasses = 'Business Classes',
  Coverages = 'Coverages',
  PostRateDefaultFactors = 'Post Rate Default Factors',
  LegalNoticeManagement = 'Legal Notice Management',
}

export const PortalAdminPages = {
  [PortalAdminPageName.Brokerage]: {
    url: 'brokerage-management',
    roles: [
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
      UserRoles.Producer,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.JrOps,
    ],
  },
  [PortalAdminPageName.User]: {
    url: 'user-management',
    roles: [UserRoles.Producer, UserRoles.SrOps, UserRoles.SystemAdmin, UserRoles.PortalAdmin],
  },
  [PortalAdminPageName.Offering]: {
    url: 'offering-management',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminPageName.UWMgmt]: {
    url: 'underwriter-management',
    roles: [
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminPageName.MoratoriumManagement]: {
    url: 'moratorium-management',
    roles: [
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.SrOps,
      UserRoles.JrOps,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminPageName.QuoteSetupPresetManagement]: {
    url: 'url-presets',
    roles: [
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.SrOps,
      UserRoles.JrOps,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminPageName.AdvancedPortalData]: {
    url: 'advanced-portal-configuration',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminPageName.Maintenance]: {
    url: 'maintenance',
    roles: [UserRoles.Producer, UserRoles.SystemAdmin, UserRoles.PortalAdmin],
  },
  [PortalAdminPageName.Alerts]: {
    url: 'alerts',
    roles: [UserRoles.Producer, UserRoles.SystemAdmin, UserRoles.PortalAdmin],
  },
  [PortalAdminPageName.FormsV2Admin]: {
    url: 'forms-v2-admin',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminPageName.SubmissionDrafts]: {
    url: 'submission-drafts',
    roles: [UserRoles.SystemAdmin, UserRoles.PortalAdmin, UserRoles.Underwriter],
  },
  [PortalAdminPageName.UiPlayground]: {
    url: 'ui-playground',
    roles: [UserRoles.SystemAdmin, UserRoles.PortalAdmin],
  },
  [PortalAdminPageName.BORTransfer]: {
    url: 'bor-transfer-requests',
    roles: [UserRoles.SystemAdmin, UserRoles.PortalAdmin],
  },
  [PortalAdminPageName.ApiClientManagement]: {
    url: 'api-client-management',
    roles: [UserRoles.SystemAdmin, UserRoles.PortalAdmin],
  },
  [PortalAdminPageName.GetAppointedPages]: {
    // Write access (create/edit/publish/…) = SystemAdmin, PortalAdmin, SrOps, LioSales.
    // Underwriter, JrOps, and LIOContractor are included for READ-ONLY access; the UI gates write
    // actions separately via useCanWriteOnboardingPages.
    url: 'get-appointed-pages',
    roles: [
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.SrOps,
      UserRoles.LioSales,
      UserRoles.Underwriter,
      UserRoles.JrOps,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminPageName.SalesAssignmentRules]: {
    url: 'sales-assignment-rules',
    roles: [
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.SrOps,
      UserRoles.JrOps,
      UserRoles.LioSales,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminPageName.BrokerageOnboarding]: {
    // Read access matches the API's `lioUserRoles` (all internal LIO users can view the queue and a
    // submission). Mutating workbench actions are gated separately by `useCanWriteOnboardingWorkbench`
    // + the server's `availableActions` contract, mirroring `onboardingOperatorRoles` on the API.
    url: 'brokerage-onboarding',
    roles: internalRoles,
  },
  [PortalAdminPageName.BoxSignTemplates]: {
    // Read access matches the API's `lioUserRoles` (all internal LIO users). Create/publish/set-default/
    // archive/edit-mappings are gated separately by `useCanWriteBoxSignTemplates` + the API's
    // `salesAssignmentRuleEditorRoles` (PortalAdmin/SystemAdmin/SrOps).
    url: 'box-sign-templates',
    roles: internalRoles,
  },
  // advanced data pages
  [PortalAdminAdvancedPortalDataPageName.LOIAvailability]: {
    url: 'loi-availability',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminAdvancedPortalDataPageName.BrokerageCommission]: {
    url: 'brokerage-commission-defaults',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminAdvancedPortalDataPageName.OfferingDefaultJurisdiction]: {
    url: 'offering-default-jurisdictions',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminAdvancedPortalDataPageName.OfferingSubjectivities]: {
    url: 'offering-subjectivities',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminAdvancedPortalDataPageName.BusinessClasses]: {
    url: 'business-classes',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminAdvancedPortalDataPageName.Coverages]: {
    url: 'coverages',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminAdvancedPortalDataPageName.PostRateDefaultFactors]: {
    url: 'post-rate-default-factors',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminAdvancedPortalDataPageName.LegalNoticeManagement]: {
    url: 'legal-notice-management',
    roles: [
      UserRoles.JrOps,
      UserRoles.SrOps,
      UserRoles.SystemAdmin,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
  },
  [PortalAdminAdvancedPortalDataPageName.UnderwritingActivityTypes]: {
    url: 'underwriting-activity-types',
    roles: [UserRoles.SrOps, UserRoles.SystemAdmin, UserRoles.PortalAdmin, UserRoles.Underwriter],
  },
};

export const drawerListItems = [
  {
    name: PortalAdminPageName.Brokerage,
    roles: PortalAdminPages[PortalAdminPageName.Brokerage].roles,
    path: PortalAdminPages[PortalAdminPageName.Brokerage].url,
  },
  {
    name: PortalAdminPageName.User,
    roles: PortalAdminPages[PortalAdminPageName.User].roles,
    path: PortalAdminPages[PortalAdminPageName.User].url,
  },
  {
    name: PortalAdminPageName.Offering,
    roles: PortalAdminPages[PortalAdminPageName.Offering].roles,
    path: PortalAdminPages[PortalAdminPageName.Offering].url,
  },
  {
    name: PortalAdminPageName.UWMgmt,
    roles: PortalAdminPages[PortalAdminPageName.UWMgmt].roles,
    path: PortalAdminPages[PortalAdminPageName.UWMgmt].url,
  },
  {
    displayName: 'Moratoriums',
    name: PortalAdminPageName.MoratoriumManagement,
    roles: PortalAdminPages[PortalAdminPageName.MoratoriumManagement].roles,
    path: PortalAdminPages[PortalAdminPageName.MoratoriumManagement].url,
  },
  {
    displayName: 'Quote Setup Presets',
    name: PortalAdminPageName.QuoteSetupPresetManagement,
    roles: PortalAdminPages[PortalAdminPageName.QuoteSetupPresetManagement].roles,
    path: PortalAdminPages[PortalAdminPageName.QuoteSetupPresetManagement].url,
  },
  {
    displayName: 'Advanced Config',
    name: PortalAdminPageName.AdvancedPortalData,
    roles: PortalAdminPages[PortalAdminPageName.AdvancedPortalData].roles,
    path: PortalAdminPages[PortalAdminPageName.AdvancedPortalData].url,
  },
  {
    name: PortalAdminPageName.Maintenance,
    roles: PortalAdminPages[PortalAdminPageName.Maintenance].roles,
    path: PortalAdminPages[PortalAdminPageName.Maintenance].url,
  },
  {
    name: PortalAdminPageName.Alerts,
    roles: PortalAdminPages[PortalAdminPageName.Alerts].roles,
    path: PortalAdminPages[PortalAdminPageName.Alerts].url,
  },
  {
    displayName: 'Forms V2 Admin',
    name: PortalAdminPageName.FormsV2Admin,
    roles: PortalAdminPages[PortalAdminPageName.FormsV2Admin].roles,
    path: PortalAdminPages[PortalAdminPageName.FormsV2Admin].url,
  },
  {
    name: PortalAdminPageName.SubmissionDrafts,
    roles: PortalAdminPages[PortalAdminPageName.SubmissionDrafts].roles,
    path: PortalAdminPages[PortalAdminPageName.SubmissionDrafts].url,
  },
  {
    name: PortalAdminPageName.UiPlayground,
    roles: PortalAdminPages[PortalAdminPageName.UiPlayground].roles,
    path: PortalAdminPages[PortalAdminPageName.UiPlayground].url,
  },
  {
    displayName: 'BOR Transfers',
    name: PortalAdminPageName.BORTransfer,
    roles: PortalAdminPages[PortalAdminPageName.BORTransfer].roles,
    path: PortalAdminPages[PortalAdminPageName.BORTransfer].url,
  },
  {
    name: PortalAdminPageName.ApiClientManagement,
    roles: PortalAdminPages[PortalAdminPageName.ApiClientManagement].roles,
    path: PortalAdminPages[PortalAdminPageName.ApiClientManagement].url,
  },
  {
    name: PortalAdminPageName.GetAppointedPages,
    roles: PortalAdminPages[PortalAdminPageName.GetAppointedPages].roles,
    path: PortalAdminPages[PortalAdminPageName.GetAppointedPages].url,
  },
  {
    name: PortalAdminPageName.SalesAssignmentRules,
    roles: PortalAdminPages[PortalAdminPageName.SalesAssignmentRules].roles,
    path: PortalAdminPages[PortalAdminPageName.SalesAssignmentRules].url,
  },
  {
    name: PortalAdminPageName.BrokerageOnboarding,
    roles: PortalAdminPages[PortalAdminPageName.BrokerageOnboarding].roles,
    path: PortalAdminPages[PortalAdminPageName.BrokerageOnboarding].url,
  },
  {
    name: PortalAdminPageName.BoxSignTemplates,
    roles: PortalAdminPages[PortalAdminPageName.BoxSignTemplates].roles,
    path: PortalAdminPages[PortalAdminPageName.BoxSignTemplates].url,
  },
];

export type PortalAdminDrawerListItem = (typeof drawerListItems)[number];

export type PortalAdminDrawerSection = {
  displayName?: string;
  items: PortalAdminDrawerListItem[];
  name: string;
};

const drawerItemsByName = drawerListItems.reduce<Record<string, PortalAdminDrawerListItem>>((itemsByName, item) => {
  itemsByName[item.name] = item;

  return itemsByName;
}, {});

const getDrawerSectionItems = (itemNames: PortalAdminPageName[]) =>
  itemNames.map((itemName) => drawerItemsByName[itemName]).filter((item): item is PortalAdminDrawerListItem => !!item);

export const portalAdminDrawerSections: PortalAdminDrawerSection[] = [
  {
    displayName: 'Brokerage',
    name: 'Brokerage Operations',
    items: getDrawerSectionItems([
      PortalAdminPageName.Brokerage,
      PortalAdminPageName.BrokerageOnboarding,
      PortalAdminPageName.GetAppointedPages,
      PortalAdminPageName.SalesAssignmentRules,
      PortalAdminPageName.BORTransfer,
    ]),
  },
  {
    displayName: 'Product',
    name: 'Product Setup',
    items: getDrawerSectionItems([
      PortalAdminPageName.Offering,
      PortalAdminPageName.QuoteSetupPresetManagement,
      PortalAdminPageName.MoratoriumManagement,
      PortalAdminPageName.FormsV2Admin,
      PortalAdminPageName.BoxSignTemplates,
      PortalAdminPageName.SubmissionDrafts,
    ]),
  },
  {
    name: 'Users',
    items: getDrawerSectionItems([PortalAdminPageName.User, PortalAdminPageName.UWMgmt]),
  },
  {
    name: 'System',
    items: getDrawerSectionItems([
      PortalAdminPageName.Alerts,
      PortalAdminPageName.Maintenance,
      PortalAdminPageName.AdvancedPortalData,
      PortalAdminPageName.ApiClientManagement,
    ]),
  },
];

/** Short blurbs shown under each section heading on the Portal Admin landing page. Keyed by section `name`. */
export const portalAdminSectionDescriptions: Record<string, string> = {
  'Brokerage Operations': 'Brokerages, onboarding, appointments, and transfers.',
  'Product Setup': 'Offerings, quote presets, forms, and moratoriums.',
  Users: 'Portal users and underwriting assignments.',
  System: 'Platform-wide settings, alerts, and configuration.',
};

/** One-line descriptions shown per link on the Portal Admin landing page. Keyed by page name. */
export const portalAdminPageDescriptions: Partial<Record<PortalAdminPageName, string>> = {
  [PortalAdminPageName.Brokerage]: 'Review brokerage details, appointments, offerings, and producer access.',
  [PortalAdminPageName.BrokerageOnboarding]:
    'Process incoming brokerage onboarding submissions through to appointment.',
  [PortalAdminPageName.GetAppointedPages]: 'Build and publish the public Get Appointed onboarding pages.',
  [PortalAdminPageName.SalesAssignmentRules]: 'Configure how new submissions are routed to sales.',
  [PortalAdminPageName.BORTransfer]: 'Review and action broker-of-record transfer requests.',
  [PortalAdminPageName.Offering]: 'Manage insurance product offerings and their configuration.',
  [PortalAdminPageName.QuoteSetupPresetManagement]: 'Create and manage reusable quote setup preset URLs.',
  [PortalAdminPageName.MoratoriumManagement]: 'Manage moratoriums that restrict quoting.',
  [PortalAdminPageName.FormsV2Admin]: 'Manage FormsV2 form definitions and editions.',
  [PortalAdminPageName.BoxSignTemplates]:
    'Link Box Sign templates, set category defaults, and configure field prefill.',
  [PortalAdminPageName.SubmissionDrafts]: 'Review and manage in-progress submission drafts.',
  [PortalAdminPageName.User]: 'Manage portal users, roles, and access.',
  [PortalAdminPageName.UWMgmt]: 'Manage underwriters, assignments, and underwriting rules.',
  [PortalAdminPageName.Alerts]: 'Publish global alerts shown across the portal.',
  [PortalAdminPageName.Maintenance]: 'Toggle maintenance mode and related messaging.',
  [PortalAdminPageName.AdvancedPortalData]: 'LOI availability, commissions, business classes, coverages, and more.',
};
